import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import client from '../../util/api-client';
import { pushToast, useDispatch } from '../../state';
import type {
  VendorPaymentCreateInput,
  VendorPaymentPurchaseOrderGroup,
  VendorPaymentSearchVendor,
} from '@repo/api-contract';

type DraftPayment = {
  id: string;
  purchaseOrderCode: string;
  vendorAccountNumber: string;
  vendorName: string;
  reference: string;
  amount: number;
  adjustment: number;
  date: string;
};

const getTodayDateInputValue = () => new Date().toISOString().slice(0, 10);

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 1, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography variant="subtitle1">{value}</Typography>
    </Box>
  );
}

export default function DraftPaymentsSidebar({
  selectedPurchaseOrder,
  searchedVendor,
  onDraftCreated,
}: {
  selectedPurchaseOrder: VendorPaymentPurchaseOrderGroup | null;
  searchedVendor: VendorPaymentSearchVendor | null;
  onDraftCreated: () => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PayVendor',
  });
  const dispatch = useDispatch();
  const [purchaseOrderCode, setPurchaseOrderCode] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [date, setDate] = useState(getTodayDateInputValue);
  const [drafts, setDrafts] = useState<DraftPayment[]>([]);
  const [creatingPayments, setCreatingPayments] = useState(false);
  const [creatingDraftIds, setCreatingDraftIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [failedDraftIds, setFailedDraftIds] = useState<Set<string>>(
    () => new Set(),
  );

  const selectedPurchaseOrderCode =
    selectedPurchaseOrder?.purchaseOrderCode ?? '';
  const selectedPurchaseOrderPayment =
    selectedPurchaseOrder?.payments[0] ?? null;
  const draftVendorAccountNumber =
    selectedPurchaseOrderPayment?.vendorAccountNumber ??
    searchedVendor?.vendorAccountNumber ??
    '';
  const draftVendorName =
    selectedPurchaseOrderPayment?.vendorName ??
    searchedVendor?.vendorName ??
    '';
  const resultingBalance = selectedPurchaseOrder?.resultingBalance ?? 0;
  const parsedAmount = Number(amount);
  const parsedAdjustment = adjustment === '' ? 0 : Number(adjustment);
  const totalDraftAmount = drafts.reduce((sum, draft) => sum + draft.amount, 0);
  const totalDraftAdjustments = drafts.reduce(
    (sum, draft) => sum + draft.adjustment,
    0,
  );
  const amountError = useMemo(() => {
    if (!amount) return '';
    if (!Number.isFinite(parsedAmount)) return t('Enter a valid amount');
    if (parsedAmount < 1) return t('Amount must be at least $1.00');
    if (selectedPurchaseOrder && parsedAmount > resultingBalance) {
      return t('Amount cannot exceed {{amount}}', {
        amount: `$${resultingBalance.toFixed(2)}`,
      });
    }
    return '';
  }, [amount, parsedAmount, resultingBalance, selectedPurchaseOrder, t]);
  const canDraft =
    purchaseOrderCode.trim().length > 0 &&
    Boolean(draftVendorAccountNumber) &&
    Boolean(draftVendorName) &&
    reference.trim().length > 0 &&
    amount.length > 0 &&
    Number.isFinite(parsedAdjustment) &&
    date.length > 0 &&
    !amountError;

  useEffect(() => {
    setPurchaseOrderCode(selectedPurchaseOrderCode);
    setReference('');
    setAmount('');
    setAdjustment('');
    setDate(getTodayDateInputValue());
  }, [selectedPurchaseOrderCode]);

  const handleAmountChange = (value: string) => {
    if (value === '') {
      setAmount('');
      return;
    }

    const nextAmount = Number(value);
    if (!Number.isFinite(nextAmount) || nextAmount < 0) return;

    setAmount(
      selectedPurchaseOrder && nextAmount > resultingBalance
        ? resultingBalance.toString()
        : value,
    );
  };

  const handleDraft = () => {
    if (!canDraft) return;

    setDrafts((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        purchaseOrderCode: purchaseOrderCode.trim(),
        vendorAccountNumber: draftVendorAccountNumber,
        vendorName: draftVendorName,
        reference: reference.trim(),
        amount: parsedAmount,
        adjustment: parsedAdjustment,
        date,
      },
    ]);
    setPurchaseOrderCode('');
    setReference('');
    setAmount('');
    setAdjustment('');
    setDate(getTodayDateInputValue());
    onDraftCreated();
  };

  const handleDeleteDraft = (id: string) => {
    if (creatingDraftIds.has(id)) return;
    setDrafts((prev) => prev.filter((draft) => draft.id !== id));
    setFailedDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleCreatePayments = async () => {
    if (drafts.length === 0 || creatingPayments) return;

    const draftsToCreate = [...drafts];
    setCreatingPayments(true);
    setCreatingDraftIds(new Set(draftsToCreate.map((draft) => draft.id)));
    setFailedDraftIds((prev) => {
      const next = new Set(prev);
      draftsToCreate.forEach((draft) => next.delete(draft.id));
      return next;
    });

    const results = await Promise.allSettled(
      draftsToCreate.map(async (draft) => {
        const payload: VendorPaymentCreateInput = {
          vendorAccountNumber: draft.vendorAccountNumber,
          vendorName: draft.vendorName,
          purchaseOrderCode: draft.purchaseOrderCode,
          payment: draft.amount,
          reference: draft.reference,
          adjustment: draft.adjustment,
          detail: '',
          captureDate: Math.floor(
            new Date(`${draft.date}T00:00:00`).getTime() / 1000,
          ),
        };

        try {
          await client.createVendorPayment.mutate(payload);
          setDrafts((prev) => prev.filter((item) => item.id !== draft.id));
        } finally {
          setCreatingDraftIds((prev) => {
            const next = new Set(prev);
            next.delete(draft.id);
            return next;
          });
        }
      }),
    );

    const failedDrafts = draftsToCreate.filter(
      (_, index) => results[index]?.status === 'rejected',
    );

    if (failedDrafts.length > 0) {
      console.error('Error creating vendor payments', results);
      setFailedDraftIds(new Set(failedDrafts.map((draft) => draft.id)));
      failedDrafts.forEach((draft) => {
        dispatch(
          pushToast({
            message: t(
              'Payment for purchase order {{purchaseOrderCode}} failed',
              {
                purchaseOrderCode: draft.purchaseOrderCode,
              },
            ),
            type: 'error',
            duration: 7000,
          }),
        );
      });
    } else {
      dispatch(
        pushToast({
          message: t(
            draftsToCreate.length === 1
              ? '{{count}} payment recorded'
              : '{{count}} payments recorded',
            { count: draftsToCreate.length },
          ),
          type: 'success',
          duration: 7000,
        }),
      );
    }

    setCreatingPayments(false);
  };

  return (
    <Box
      className="border border-gray-200 border-solid rounded shadow-xl"
      sx={{
        width: 280,
        flexShrink: 0,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('Draft Payments')}
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label={t('Purchase Order #')}
            value={purchaseOrderCode}
            placeholder={t('Select a purchase order')}
            helperText={t('Select purchase order from table')}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Ref')}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Amount')}
            type="number"
            value={amount}
            onChange={(event) => handleAmountChange(event.target.value)}
            disabled={!purchaseOrderCode.trim()}
            error={Boolean(amountError)}
            helperText={
              amountError ||
              (selectedPurchaseOrder
                ? t('Available: {{amount}}', {
                    amount: `$${resultingBalance.toFixed(2)}`,
                  })
                : t('Manual purchase order has no balance limit'))
            }
            inputProps={{
              min: 1,
              max: selectedPurchaseOrder ? resultingBalance : undefined,
              step: '0.01',
            }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Adjustment')}
            type="number"
            value={adjustment}
            onChange={(event) => setAdjustment(event.target.value)}
            disabled={!purchaseOrderCode.trim()}
            inputProps={{ step: '0.01' }}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Date')}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            disabled={!purchaseOrderCode.trim()}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Button
            variant="outlined"
            color="primary"
            disabled={!canDraft}
            onClick={handleDraft}
            fullWidth
          >
            {t('Draft')}
          </Button>
        </Stack>
      </Box>

      <Stack
        spacing={1}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {drafts.map((draft) =>
          (() => {
            const isCreating = creatingDraftIds.has(draft.id);
            const isFailed = failedDraftIds.has(draft.id);

            return (
              <Chip
                key={draft.id}
                icon={
                  isCreating ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
                label={`${draft.purchaseOrderCode} | $${draft.amount.toFixed(2)}`}
                color={isFailed ? 'error' : 'primary'}
                onDelete={
                  isCreating ? undefined : () => handleDeleteDraft(draft.id)
                }
                sx={{ justifyContent: 'space-between', borderRadius: 1 }}
              />
            );
          })(),
        )}
      </Stack>

      <Box
        sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Stack spacing={2}>
          <SummaryRow
            label={t('Total Payments')}
            value={`$${totalDraftAmount.toFixed(2)}`}
          />
          <SummaryRow
            label={t('Total Adjustments')}
            value={`$${totalDraftAdjustments.toFixed(2)}`}
          />
        </Stack>

        <Button
          variant="contained"
          color="primary"
          disabled={drafts.length === 0 || creatingPayments}
          onClick={handleCreatePayments}
          fullWidth
        >
          {t('Record')}
        </Button>
      </Box>
    </Box>
  );
}
